import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { useTranslation } from './i18n/LanguageContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom markers
function AirplaneMarker({ position }) {
  const icon = L.divIcon({ html: '<div style="font-size: 40px;">✈️</div>', className: '', iconSize: [40, 40] });
  return <Marker position={position} icon={icon} />;
}

function TaxiMarker({ position }) {
  const icon = L.divIcon({ html: '<div style="font-size: 35px;">🚕</div>', className: '', iconSize: [35, 35] });
  return <Marker position={position} icon={icon} />;
}

function LocationMarker({ position, icon, label }) {
  const markerIcon = L.divIcon({ 
    html: `<div style="font-size: 40px; text-align: center;">${icon}<div style="font-size: 12px; color: #333; font-weight: bold; background: white; padding: 2px 5px; border-radius: 5px; margin-top: 5px;">${label}</div></div>`, 
    className: '', 
    iconSize: [60, 60] 
  });
  return <Marker position={position} icon={markerIcon} />;
}

function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && zoom) map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

function MoroccoConversationGame() {
  const { t } = useTranslation();
  // Add CSS animations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% { transform: translate(-50%, -50%) translateY(0); }
        40% { transform: translate(-50%, -50%) translateY(-10px); }
        60% { transform: translate(-50%, -50%) translateY(-5px); }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-20px); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Game state
  const [scene, setScene] = useState('country-input'); // country-input, flying, welcome, main-menu, map-view, taxi-bargain, taxi-driving, hotel-arrival, hotel-checkin, hotel-reservation, hotel-room-select, hotel-room, restaurant-map, restaurant-arrival, restaurant-greeting, restaurant-menu, restaurant-cooking, restaurant-eating, restaurant-action
  const [country, setCountry] = useState('');
  const [homeCoords, setHomeCoords] = useState(null);
  
  // Character & Conversation
  const [currentCharacter, setCurrentCharacter] = useState(null);
  const [characterMessage, setCharacterMessage] = useState('');
  const [userChoices, setUserChoices] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  
  // Money & Tracking
  const [wallet, setWallet] = useState(500);
  const [agreedTaxiPrice, setAgreedTaxiPrice] = useState(null);
  const [currentActivity, setCurrentActivity] = useState(null);
  
  // Map & Animation
  const [airplanePos, setAirplanePos] = useState(null);
  const [taxiPos, setTaxiPos] = useState(null);
  const [mapCenter, setMapCenter] = useState([30, -20]);
  const [mapZoom, setMapZoom] = useState(3);
  const [showRoute, setShowRoute] = useState(false);
  const [routeStart, setRouteStart] = useState(null);
  const [routeEnd, setRouteEnd] = useState(null);
  const [currentLocation, setCurrentLocation] = useState('In Flight');
  
  // Restaurant & Hotel
  const [foodCart, setFoodCart] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [hasReservation, setHasReservation] = useState(null);
  const [showDoneEatingPopup, setShowDoneEatingPopup] = useState(false);
  const [showActionButton, setShowActionButton] = useState(false);
  
  const countryCoords = {
    'USA': [37.0902, -95.7129],
    'UK': [55.3781, -3.4360],
    'France': [46.2276, 2.2137],
    'Germany': [51.1657, 10.4515],
    'Spain': [40.4637, -3.7492],
    'Italy': [41.8719, 12.5674],
  };
  
  const moroccoCoords = [33.5731, -7.5898];
  const hotelCoords = [33.5831, -7.5898];
  const restaurantCoords = [33.5731, -7.6898];
  const mosqueCoords = [33.5931, -7.5998];
  const beachCoords = [33.6031, -7.6298];
  const soukCoords = [33.5631, -7.5798];
  
  const menuItems = {
    mains: [
      { name: 'Chicken Tajine', price: 45, icon: '🍲' },
      { name: 'Beef Couscous', price: 50, icon: '🥘' },
      { name: 'Lamb Mechoui', price: 65, icon: '🍖' },
      { name: 'Fish Tagra', price: 55, icon: '🐟' },
    ],
    sides: [
      { name: 'Bread', price: 5, icon: '🥖' },
      { name: 'Zaalouk', price: 10, icon: '🥗' },
      { name: 'Moroccan Salad', price: 10, icon: '🥗' },
    ],
    drinks: [
      { name: 'Mint Tea', price: 8, icon: '🍵' },
      { name: 'Orange Juice', price: 12, icon: '🧃' },
      { name: 'Water', price: 5, icon: '💧' },
    ]
  };
  
  const roomTypes = [
    { name: 'Standard Room', price: 150, features: 'Single bed, WiFi, AC' },
    { name: 'Deluxe Room', price: 250, features: 'Queen bed, WiFi, AC, Balcony' },
    { name: 'Suite', price: 400, features: 'King bed, WiFi, AC, Living room, Ocean view' },
  ];
  
  // Start journey
  const startJourney = () => {
    if (!country.trim()) {
      alert(t('mcg_enter_country_alert'));
      return;
    }
    const coords = countryCoords[country] || countryCoords['USA'];
    setHomeCoords(coords);
    setAirplanePos(coords);
    setScene('flying');
    animateAirplane(coords);
  };
  
  const animateAirplane = (start) => {
    const duration = 4000;
    const steps = 60;
    let currentStep = 0;
    
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      if (progress >= 1) {
        clearInterval(interval);
        arriveInMorocco();
        return;
      }
      
      const lat = start[0] + (moroccoCoords[0] - start[0]) * progress;
      const lng = start[1] + (moroccoCoords[1] - start[1]) * progress;
      setAirplanePos([lat, lng]);
      setMapCenter([lat, lng]);
    }, duration / steps);
  };
  
  const arriveInMorocco = () => {
    setCurrentLocation('Casablanca');
    setScene('welcome');
    setCurrentCharacter('🧕');
    setCharacterMessage('Welcome to Morocco! How are you feeling?');
    setUserChoices([
      { text: 'I am good', value: 'good' },
      { text: 'I am tired', value: 'tired' },
      { text: 'I am happy', value: 'happy' },
      { text: 'I am hungry', value: 'hungry' },
    ]);
  };
  
  const handleWelcomeResponse = (choice) => {
    let response = '';
    if (choice === 'good') response = "That's wonderful to hear! You arrived safely.";
    else if (choice === 'tired') response = "Oh, I'm sorry to hear that. Let's make your experience relaxing.";
    else if (choice === 'happy') response = "Amazing! Morocco is excited to have you.";
    else if (choice === 'hungry') response = "Perfect! Morocco has delicious food waiting for you.";
    
    setCharacterMessage(response);
    setUserChoices([{ text: 'Continue', value: 'continue' }]);
  };
  
  const showMainMenu = () => {
    setScene('main-menu');
    setCurrentCharacter('🧕');
    setCharacterMessage('What would you like to do now?');
    setUserChoices([
      { text: '🏨 Find hotel', value: 'hotel' },
      { text: '🍽️ Go eat', value: 'eat' },
      { text: '✈️ Travel', value: 'travel' },
      { text: '🕌 Visit attractions', value: 'attractions' },
      { text: '🧘 Relax', value: 'relax' },
      { text: '🎯 Explore', value: 'explore' },
    ]);
  };
  
  const handleMainMenuChoice = (choice) => {
    setCurrentActivity(choice);
    
    if (choice === 'hotel') {
      setScene('map-view');
      setRouteStart(moroccoCoords);
      setRouteEnd(hotelCoords);
      setShowRoute(true);
      setMapCenter(moroccoCoords);
      setMapZoom(13);
      setCurrentCharacter(null);
      setCharacterMessage('');
      setUserChoices([]);
      
      setTimeout(() => {
        setCharacterMessage('You will need a taxi to go to your hotel. I have some prices for you.');
        setUserChoices([
          { text: 'Good deal: 20 MAD ($2)', value: 20 },
          { text: 'Fair price: 30 MAD ($3)', value: 30 },
          { text: 'Tourist price: 40 MAD ($4)', value: 40 },
        ]);
        setScene('taxi-bargain');
        setCurrentCharacter('🧕');
      }, 3000);
    } else if (choice === 'eat') {
      setScene('restaurant-map');
      setRouteStart(moroccoCoords);
      setRouteEnd(restaurantCoords);
      setShowRoute(true);
      setMapCenter(moroccoCoords);
      setMapZoom(13);
      setCurrentCharacter(null);
      setCharacterMessage('');
      setUserChoices([]);
      
      setTimeout(() => {
        setCharacterMessage('You will need a taxi to go to the restaurant. Here are the prices:');
        setUserChoices([
          { text: 'Walk (Free)', value: 0 },
          { text: '10 MAD ($1)', value: 10 },
          { text: '15 MAD ($1.5)', value: 15 },
          { text: '20 MAD ($2)', value: 20 },
        ]);
        setScene('taxi-bargain');
        setCurrentCharacter('🧕');
      }, 3000);
    }
  };
  
  const handleFirstBargain = (price) => {
    setAgreedTaxiPrice(price);
    
    if (price === 0) {
      // Walking
      setCharacterMessage('Great! Enjoy your walk to the destination.');
      setTimeout(() => {
        if (currentActivity === 'hotel') arriveAtHotel();
        else if (currentActivity === 'eat') arriveAtRestaurant();
      }, 1000);
    } else {
      setCharacterMessage(`We are very happy to have you here. Taxi will take you for ${price} MAD.`);
      setUserChoices([{ text: 'Continue', value: 'continue' }]);
    }
  };
  
  const showTaxiDriver = () => {
    setScene('taxi-driving');
    setCurrentCharacter('🚕');
    setCharacterMessage(`Salam! Do you want to go to the ${currentActivity === 'hotel' ? 'hotel' : 'restaurant'}? Here are my prices:`);
    setUserChoices([
      { text: '20 MAD', value: 20 },
      { text: '30 MAD', value: 30 },
      { text: '40 MAD', value: 40 },
    ]);
    setTaxiPos(moroccoCoords);
  };
  
  const handleTaxiNegotiation = (price) => {
    if (price === agreedTaxiPrice) {
      setCharacterMessage('Mar7ba! (Welcome!) Let\'s go.');
      animateTaxi();
    } else {
      setCharacterMessage(`We already agreed on ${agreedTaxiPrice} MAD.`);
      setUserChoices([{ text: 'OK, let\'s go', value: 'continue' }]);
    }
  };
  
  const animateTaxi = () => {
    const duration = 3000;
    const steps = 40;
    let currentStep = 0;
    
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      if (progress >= 1) {
        clearInterval(interval);
        if (currentActivity === 'hotel') arriveAtHotel();
        else if (currentActivity === 'eat') arriveAtRestaurant();
        return;
      }
      
      const lat = routeStart[0] + (routeEnd[0] - routeStart[0]) * progress;
      const lng = routeStart[1] + (routeEnd[1] - routeStart[1]) * progress;
      setTaxiPos([lat, lng]);
    }, duration / steps);
  };
  
  const arriveAtHotel = () => {
    setScene('hotel-arrival');
    setShowRoute(false);
    setTaxiPos(null);
    setCurrentCharacter('🚕');
    setCharacterMessage('Thank you! Please pay the amount we agreed on.');
    setUserChoices([
      { text: `20 MAD`, value: 20 },
      { text: `30 MAD`, value: 30 },
      { text: `40 MAD`, value: 40 },
    ]);
  };
  
  const handleTaxiPayment = (price) => {
    if (price !== agreedTaxiPrice) {
      setCharacterMessage(`No, no. We agreed on ${agreedTaxiPrice} MAD.`);
    } else {
      setWallet(wallet - price);
      setCharacterMessage('Thank you! Have a great stay!');
      setTimeout(() => {
        enterHotel();
      }, 1500);
    }
  };
  
  const enterHotel = () => {
    setScene('hotel-checkin');
    setCurrentCharacter('🧑‍💼');
    setCharacterMessage('Welcome! Do you have a reservation, or would you like to reserve a room?');
    setUserChoices([
      { text: 'I have a reservation', value: 'has-reservation' },
      { text: 'I want to reserve a room', value: 'new-reservation' },
    ]);
  };
  
  const handleReservationChoice = (choice) => {
    if (choice === 'has-reservation') {
      setScene('hotel-reservation');
      setCharacterMessage('No problem! May I have your details?');
      setUserChoices([
        { text: 'Here is my reservation number', value: 'number' },
        { text: 'I booked under my name', value: 'name' },
        { text: 'I booked online', value: 'online' },
        { text: 'I am not sure, can you check?', value: 'check' },
      ]);
    } else {
      setScene('hotel-room-select');
      setCharacterMessage('Great! Here are the available rooms today:');
      setUserChoices(roomTypes.map(room => ({
        text: `${room.name} - ${room.price} MAD`,
        value: room.name,
        details: room.features
      })));
    }
  };
  
  const handleReservationDetails = (choice) => {
    let response = '';
    if (choice === 'number') response = 'Please enter it... Searching... Yes! Your room is ready.';
    else if (choice === 'name') response = 'What is your name?... Searching... Found it! You booked a Deluxe Room.';
    else if (choice === 'online') response = 'Did you use Booking.com, Expedia, or our website?... Checking... Yes, confirmed.';
    else response = 'Give me your phone, email, passport, or name... Searching... Found it!';
    
    setCharacterMessage(response);
    setTimeout(() => {
      setCharacterMessage('Here is your room key. Would you like help with luggage?');
      setUserChoices([
        { text: 'Yes, please', value: 'yes' },
        { text: 'No, thank you', value: 'no' },
      ]);
    }, 2000);
  };
  
  const handleLuggageHelp = (choice) => {
    const response = choice === 'yes' ? 'I\'ll send someone right away!' : 'No problem! Enjoy your stay.';
    setCharacterMessage(response);
    setUserChoices([{ text: 'Go to my room', value: 'room' }]);
  };
  
  const goToHotelRoom = () => {
    setScene('hotel-room');
    setCurrentCharacter('🛏️');
    setCharacterMessage('You can rest or choose your next activity.');
    setUserChoices([
      { text: '😴 Rest', value: 'rest' },
      { text: '🚿 Shower', value: 'shower' },
      { text: '🍽️ Go eat', value: 'eat' },
      { text: '🎯 Explore', value: 'explore' },
      { text: '🕌 Visit attractions', value: 'attractions' },
      { text: '🚕 Call taxi', value: 'taxi' },
      { text: '💰 Check budget', value: 'budget' },
    ]);
  };
  
  const handleRoomSelection = (roomName) => {
    const room = roomTypes.find(r => r.name === roomName);
    setSelectedRoomType(room);
    setWallet(wallet - room.price);
    setCharacterMessage(`Perfect choice! ${room.features}. Here is your key.`);
    setUserChoices([{ text: 'Go to my room', value: 'room' }]);
  };
  
  const arriveAtRestaurant = () => {
    if (agreedTaxiPrice && agreedTaxiPrice > 0) {
      setScene('hotel-arrival');
      setShowRoute(false);
      setTaxiPos(null);
      setCurrentCharacter('🚕');
      setCharacterMessage('We arrived! Please pay the agreed amount.');
      setUserChoices([{ text: `Pay ${agreedTaxiPrice} MAD`, value: agreedTaxiPrice }]);
    } else {
      enterRestaurant();
    }
  };
  
  const payRestaurantTaxi = () => {
    setWallet(wallet - agreedTaxiPrice);
    enterRestaurant();
  };
  
  const enterRestaurant = () => {
    setScene('restaurant-greeting');
    setCurrentCharacter('👨‍🍳');
    setCharacterMessage('Salam! Welcome to our restaurant! How are you today?');
    setUserChoices([
      { text: 'I am good', value: 'good' },
      { text: 'I am tired', value: 'tired' },
      { text: 'I am excited', value: 'excited' },
      { text: 'I am hungry', value: 'hungry' },
    ]);
  };
  
  const handleRestaurantGreeting = (choice) => {
    let response = '';
    if (choice === 'good') response = 'That\'s great! Let me show you our menu.';
    else if (choice === 'tired') response = 'Oh! Our food will give you energy!';
    else if (choice === 'excited') response = 'Wonderful! You\'ll love our dishes!';
    else response = 'Perfect! Let\'s get you some delicious food!';
    
    setCharacterMessage(response);
    setUserChoices([{ text: 'Continue', value: 'continue' }]);
  };
  
  const showRestaurantMenu = () => {
    setScene('restaurant-menu');
    setCharacterMessage('Here is our menu. What would you like?');
  };
  
  const addToFoodCart = (item) => {
    setFoodCart([...foodCart, item]);
  };
  
  const startCooking = () => {
    setScene('restaurant-cooking');
    setCharacterMessage('Preparing your delicious food...');
    
    setTimeout(() => {
      setScene('restaurant-eating');
      setCharacterMessage('Your food is ready! Enjoy.');
      setShowActionButton(true);
      
      setTimeout(() => {
        setShowDoneEatingPopup(true);
      }, 3000);
    }, 2000);
  };
  
  const handleRestaurantAction = (action) => {
    setShowActionButton(false);
    
    if (action === 'done') {
      showRestaurantBill();
    } else if (action === 'more') {
      setScene('restaurant-menu');
      setCharacterMessage('What else would you like?');
    } else if (action === 'bill') {
      showRestaurantBill();
    } else if (action === 'leave') {
      checkoutRestaurant();
    }
  };
  
  const showRestaurantBill = () => {
    const total = foodCart.reduce((sum, item) => sum + item.price, 0);
    setScene('restaurant-action');
    setCharacterMessage('Here is your bill. Ready to checkout?');
  };
  
  const checkoutRestaurant = () => {
    const total = foodCart.reduce((sum, item) => sum + item.price, 0);
    setWallet(wallet - total);
    setCharacterMessage('Thank you for eating here! Come again!');
    
    setTimeout(() => {
      setFoodCart([]);
      showMainMenu();
    }, 2000);
  };
  
  const calculateTotal = () => {
    return foodCart.reduce((sum, item) => sum + item.price, 0);
  };
  
  // Render scene content
  const renderScene = () => {
    // Country input
    if (scene === 'country-input') {
      return (
        <div style={styles.fullScreenCenter}>
          <h1 style={styles.title}>🌍 {t('mcg_journey_title')}</h1>
          <div style={{fontSize: '14px', color: 'white', marginBottom: '20px', opacity: 0.8}}>
            v2.0.{Date.now()} - With Map Icons & Conversation Flow
          </div>
          <div style={styles.inputBox}>
            <span style={{fontSize: '24px'}}>📍</span>
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder={t('mcg_enter_country_placeholder')}
              style={styles.input}
            />
          </div>
          <button onClick={startJourney} style={styles.startBtn}>✈️ {t('mcg_start_journey')}</button>
        </div>
      );
    }
    
    // Flying scene - GAME STYLE
    if (scene === 'flying' && homeCoords) {
      return (
        <div style={styles.gameMapContainer}>
          <MapContainer 
            center={mapCenter} 
            zoom={mapZoom} 
            style={styles.map}
            zoomControl={false}
            attributionControl={false}
          >
            {/* NO STREETS - Just game background */}
            <TileLayer 
              url="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==" 
              opacity={0}
            />
            <Marker position={homeCoords}><Popup>📍 {country}</Popup></Marker>
            <Marker position={moroccoCoords}><Popup>🇲🇦 Morocco</Popup></Marker>
            {airplanePos && <AirplaneMarker position={airplanePos} />}
            <MapController center={mapCenter} zoom={mapZoom} />
          </MapContainer>
          
          {/* Game decorations */}
          <div style={styles.mapDecoration}>
            <div style={{...styles.cloud, top: '15%', left: '10%', animationDelay: '0s'}}>☁️</div>
            <div style={{...styles.cloud, top: '25%', right: '15%', animationDelay: '2s'}}>☁️</div>
            <div style={{...styles.cloud, top: '60%', left: '30%', animationDelay: '4s'}}>☁️</div>
            <div style={styles.sun}>☀️</div>
          </div>
        </div>
      );
    }
    
    // Map views (taxi, routes) - TOP-DOWN GAME VIEW WITH STREETS
    if (['map-view', 'restaurant-map', 'taxi-driving'].includes(scene)) {
      return (
        <div style={styles.topDownMapContainer}>
          {/* Streets/Roads Background */}
          <svg style={styles.streetsSVG}>
            {/* Horizontal streets */}
            <line x1="0" y1="30%" x2="100%" y2="30%" stroke="#8B7355" strokeWidth="40" opacity="0.3" />
            <line x1="0" y1="50%" x2="100%" y2="50%" stroke="#8B7355" strokeWidth="50" opacity="0.4" />
            <line x1="0" y1="70%" x2="100%" y2="70%" stroke="#8B7355" strokeWidth="40" opacity="0.3" />
            
            {/* Vertical streets */}
            <line x1="25%" y1="0" x2="25%" y2="100%" stroke="#8B7355" strokeWidth="45" opacity="0.35" />
            <line x1="50%" y1="0" x2="50%" y2="100%" stroke="#8B7355" strokeWidth="50" opacity="0.4" />
            <line x1="75%" y1="0" x2="75%" y2="100%" stroke="#8B7355" strokeWidth="45" opacity="0.35" />
          </svg>
          
          {/* Buildings and Locations */}
          <div style={styles.buildingsLayer}>
            {/* Hotel */}
            <div style={{...styles.gameBuilding, top: '25%', left: '45%'}}>
              <div style={styles.buildingIcon}>🏨</div>
              <div style={styles.buildingLabel}>{t('mcg_loc_hotel')}</div>
            </div>
            
            {/* Restaurant */}
            <div style={{...styles.gameBuilding, top: '45%', left: '20%'}}>
              <div style={styles.buildingIcon}>🍽️</div>
              <div style={styles.buildingLabel}>{t('mcg_loc_restaurant')}</div>
            </div>
            
            {/* Mosque */}
            <div style={{...styles.gameBuilding, top: '20%', left: '70%'}}>
              <div style={styles.buildingIcon}>🕌</div>
              <div style={styles.buildingLabel}>{t('mcg_loc_mosque')}</div>
            </div>
            
            {/* Beach */}
            <div style={{...styles.gameBuilding, top: '10%', left: '25%'}}>
              <div style={styles.buildingIcon}>🏖️</div>
              <div style={styles.buildingLabel}>{t('mcg_loc_beach')}</div>
            </div>
            
            {/* Souk */}
            <div style={{...styles.gameBuilding, top: '65%', left: '70%'}}>
              <div style={styles.buildingIcon}>🛍️</div>
              <div style={styles.buildingLabel}>{t('mcg_loc_souk')}</div>
            </div>
            
            {/* You are here */}
            <div style={{...styles.gameBuilding, top: '50%', left: '50%'}}>
              <div style={styles.buildingIcon}>📍</div>
              <div style={styles.buildingLabel}>{t('mcg_loc_you_are_here')}</div>
            </div>
            
            {/* Taxi - animated along street */}
            {taxiPos && (
              <div style={{...styles.gameTaxi, 
                top: scene === 'taxi-driving' ? `${45 + Math.random() * 2}%` : '50%',
                left: scene === 'taxi-driving' ? `${35 + Math.random() * 2}%` : '50%'
              }}>
                🚕
              </div>
            )}
          </div>
          
          {/* Decorative elements */}
          <div style={styles.mapDecoration}>
            <div style={{...styles.cloud, top: '5%', left: '10%'}}>☁️</div>
            <div style={{...styles.cloud, top: '5%', right: '10%'}}>☁️</div>
            <div style={styles.sun}>☀️</div>
          </div>
        </div>
      );
    }
    
    // Character conversation scenes - For taxi-bargain, show over map
    if (scene === 'taxi-bargain' && (currentCharacter || characterMessage)) {
      return (
        <>
          {/* Show TOP-DOWN map in background */}
          <div style={styles.topDownMapContainer}>
            {/* Streets */}
            <svg style={styles.streetsSVG}>
              <line x1="0" y1="30%" x2="100%" y2="30%" stroke="#8B7355" strokeWidth="40" opacity="0.3" />
              <line x1="0" y1="50%" x2="100%" y2="50%" stroke="#8B7355" strokeWidth="50" opacity="0.4" />
              <line x1="0" y1="70%" x2="100%" y2="70%" stroke="#8B7355" strokeWidth="40" opacity="0.3" />
              <line x1="25%" y1="0" x2="25%" y2="100%" stroke="#8B7355" strokeWidth="45" opacity="0.35" />
              <line x1="50%" y1="0" x2="50%" y2="100%" stroke="#8B7355" strokeWidth="50" opacity="0.4" />
              <line x1="75%" y1="0" x2="75%" y2="100%" stroke="#8B7355" strokeWidth="45" opacity="0.35" />
            </svg>
            
            {/* Buildings */}
            <div style={styles.buildingsLayer}>
              <div style={{...styles.gameBuilding, top: '25%', left: '45%'}}>
                <div style={styles.buildingIcon}>🏨</div>
                <div style={styles.buildingLabel}>{t('mcg_loc_hotel')}</div>
              </div>
              <div style={{...styles.gameBuilding, top: '45%', left: '20%'}}>
                <div style={styles.buildingIcon}>🍽️</div>
                <div style={styles.buildingLabel}>{t('mcg_loc_restaurant')}</div>
              </div>
              <div style={{...styles.gameBuilding, top: '20%', left: '70%'}}>
                <div style={styles.buildingIcon}>🕌</div>
                <div style={styles.buildingLabel}>{t('mcg_loc_mosque')}</div>
              </div>
              <div style={{...styles.gameBuilding, top: '10%', left: '25%'}}>
                <div style={styles.buildingIcon}>🏖️</div>
                <div style={styles.buildingLabel}>{t('mcg_loc_beach')}</div>
              </div>
              <div style={{...styles.gameBuilding, top: '65%', left: '70%'}}>
                <div style={styles.buildingIcon}>🛍️</div>
                <div style={styles.buildingLabel}>{t('mcg_loc_souk')}</div>
              </div>
              <div style={{...styles.gameBuilding, top: '50%', left: '50%'}}>
                <div style={styles.buildingIcon}>📍</div>
                <div style={styles.buildingLabel}>{t('mcg_loc_you_are_here')}</div>
              </div>
            </div>
            
            {/* Decorative */}
            <div style={styles.mapDecoration}>
              <div style={{...styles.cloud, top: '5%', left: '10%'}}>☁️</div>
              <div style={{...styles.cloud, top: '5%', right: '10%'}}>☁️</div>
              <div style={styles.sun}>☀️</div>
            </div>
          </div>
          
          {/* Overlay conversation */}
          <div style={styles.conversationOverlay}>
            {currentCharacter && (
              <div style={styles.characterContainer}>
                <div style={styles.character}>{currentCharacter}</div>
              </div>
            )}
            
            {characterMessage && (
              <div style={styles.speechBubble}>
                {characterMessage}
              </div>
            )}
            
            {userChoices.length > 0 && (
              <div style={styles.choicesContainer}>
                {userChoices.map((choice, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleUserChoice(choice.value)}
                    style={styles.choiceButton}
                  >
                    {choice.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }
    
    // Character conversation scenes
    if (['welcome', 'main-menu', 'hotel-arrival', 'hotel-checkin', 'hotel-reservation', 'hotel-room-select', 'restaurant-greeting', 'restaurant-cooking', 'restaurant-eating', 'restaurant-action'].includes(scene)) {
      return (
        <div style={styles.conversationScene}>
          {currentCharacter && (
            <div style={styles.characterContainer}>
              <div style={styles.character}>{currentCharacter}</div>
              {showActionButton && (
                <button onClick={() => setShowActionButton(false)} style={styles.actionButton}>⚙️</button>
              )}
            </div>
          )}
          
          {characterMessage && (
            <div style={styles.speechBubble}>
              {characterMessage}
            </div>
          )}
          
          {userChoices.length > 0 && (
            <div style={styles.choicesContainer}>
              {userChoices.map((choice, idx) => (
                <button
                  key={idx}
                  onClick={() => handleUserChoice(choice.value)}
                  style={styles.choiceButton}
                >
                  {choice.text}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }
    
    // Restaurant menu
    if (scene === 'restaurant-menu') {
      return (
        <div style={styles.menuScene}>
          <div style={styles.character}>👨‍🍳</div>
          <div style={styles.speechBubble}>{characterMessage}</div>
          
          <div style={styles.menuContainer}>
            <div style={styles.menuSection}>
              <h3>{t('mcg_main_dishes')}</h3>
              {menuItems.mains.map((item, idx) => (
                <div key={idx} onClick={() => addToFoodCart(item)} style={styles.menuItem}>
                  <span>{item.icon} {item.name}</span>
                  <span>{item.price} MAD</span>
                </div>
              ))}
            </div>
            
            <div style={styles.menuSection}>
              <h3>{t('mcg_sides')}</h3>
              {menuItems.sides.map((item, idx) => (
                <div key={idx} onClick={() => addToFoodCart(item)} style={styles.menuItem}>
                  <span>{item.icon} {item.name}</span>
                  <span>{item.price} MAD</span>
                </div>
              ))}
            </div>
            
            <div style={styles.menuSection}>
              <h3>{t('mcg_drinks')}</h3>
              {menuItems.drinks.map((item, idx) => (
                <div key={idx} onClick={() => addToFoodCart(item)} style={styles.menuItem}>
                  <span>{item.icon} {item.name}</span>
                  <span>{item.price} MAD</span>
                </div>
              ))}
            </div>
          </div>
          
          {foodCart.length > 0 && (
            <div style={styles.orderButtons}>
              <button onClick={startCooking} style={{...styles.choiceButton, background: '#4CAF50'}}>
                Order & Cook ({calculateTotal()} MAD)
              </button>
            </div>
          )}
        </div>
      );
    }
    
    // Hotel room
    if (scene === 'hotel-room') {
      return (
        <div style={styles.conversationScene}>
          <div style={styles.character}>🛏️</div>
          <div style={styles.speechBubble}>{characterMessage}</div>
          <div style={styles.choicesContainer}>
            {userChoices.map((choice, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (choice.value === 'eat') {
                    handleMainMenuChoice('eat');
                  } else if (choice.value === 'budget') {
                    alert(`Your budget: ${wallet} MAD remaining`);
                  } else {
                    alert(`${choice.text} - Coming soon!`);
                  }
                }}
                style={styles.choiceButton}
              >
                {choice.text}
              </button>
            ))}
          </div>
        </div>
      );
    }
    
    return <div style={styles.loading}>{t('amazigh_loading')}</div>;
  };
  
  const handleUserChoice = (value) => {
    // Handle different choice types
    if (scene === 'welcome') {
      if (value === 'continue') showMainMenu();
      else handleWelcomeResponse(value);
    } else if (scene === 'main-menu') {
      handleMainMenuChoice(value);
    } else if (scene === 'taxi-bargain') {
      handleFirstBargain(value);
    } else if (scene === 'taxi-driving') {
      if (value === 'continue') animateTaxi();
      else handleTaxiNegotiation(value);
    } else if (scene === 'hotel-arrival') {
      if (currentActivity === 'eat') payRestaurantTaxi();
      else handleTaxiPayment(value);
    } else if (scene === 'hotel-checkin') {
      handleReservationChoice(value);
    } else if (scene === 'hotel-reservation') {
      handleReservationDetails(value);
      setTimeout(() => handleLuggageHelp('yes'), 2500);
    } else if (scene === 'hotel-room-select') {
      handleRoomSelection(value);
    } else if (scene === 'restaurant-greeting') {
      if (value === 'continue') showRestaurantMenu();
      else handleRestaurantGreeting(value);
    }
    
    if (userChoices.some(c => c.value === 'room')) {
      goToHotelRoom();
    }
  };
  
  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
      {renderScene()}
      
      {/* HUD */}
      {scene !== 'country-input' && (
        <div style={styles.hud}>
          <h3 style={{textAlign: 'center', color: '#FFD700', marginBottom: '15px'}}>💰 Budget</h3>
          <div style={styles.hudItem}>
            <span>💵 Wallet:</span>
            <span style={{color: '#FFD700', fontWeight: 'bold'}}>{wallet} MAD</span>
          </div>
          <div style={styles.hudItem}>
            <span>📍 Location:</span>
            <span style={{color: '#FFD700', fontWeight: 'bold'}}>{currentLocation}</span>
          </div>
        </div>
      )}
      
      {/* Food Cart */}
      {foodCart.length > 0 && scene === 'restaurant-menu' && (
        <div style={styles.cart}>
          <h3 style={{marginBottom: '15px'}}>🛒 Your Order</h3>
          {foodCart.map((item, idx) => (
            <div key={idx} style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
              <span>{item.icon} {item.name}</span>
              <span>{item.price} MAD</span>
            </div>
          ))}
          <hr style={{margin: '10px 0'}} />
          <div style={{display: 'flex', justifyContent: 'space-between', fontWeight: 'bold'}}>
            <span>{t('mcg_total')}</span>
            <span>{calculateTotal()} MAD</span>
          </div>
        </div>
      )}
      
      {/* Done Eating Popup */}
      {showDoneEatingPopup && (
        <div style={styles.popup}>
          <h3>{t('mcg_done_eating')}</h3>
          <button onClick={() => { setShowDoneEatingPopup(false); setShowActionButton(true); }} style={{...styles.choiceButton, background: '#4CAF50', margin: '10px'}}>
            Yes
          </button>
          <button onClick={() => setShowDoneEatingPopup(false)} style={{...styles.choiceButton, background: '#f44336', margin: '10px'}}>
            No
          </button>
        </div>
      )}
      
      {/* Action Menu */}
      {showActionButton && scene === 'restaurant-eating' && (
        <div style={styles.actionMenu}>
          <h3 style={{marginBottom: '15px'}}>{t('mcg_actions')}</h3>
          <button onClick={() => handleRestaurantAction('done')} style={styles.actionMenuItem}>✅ Done eating</button>
          <button onClick={() => handleRestaurantAction('more')} style={styles.actionMenuItem}>🍽️ Eat something else</button>
          <button onClick={() => handleRestaurantAction('bill')} style={styles.actionMenuItem}>🧾 Bills</button>
          <button onClick={() => handleRestaurantAction('leave')} style={styles.actionMenuItem}>🚶 Go somewhere else</button>
        </div>
      )}
    </div>
  );
}

const styles = {
  fullScreenCenter: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    zIndex: 5000,
  },
  title: {
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '30px',
  },
  inputBox: {
    background: 'white',
    padding: '20px 30px',
    borderRadius: '30px',
    display: 'flex',
    gap: '15px',
    marginBottom: '30px',
  },
  input: {
    border: 'none',
    outline: 'none',
    fontSize: '18px',
    width: '300px',
    color: '#333',
    backgroundColor: 'transparent',
  },
  startBtn: {
    background: '#FFD700',
    color: '#333',
    border: 'none',
    padding: '20px 50px',
    borderRadius: '30px',
    fontSize: '20px',
    fontWeight: 'bold',
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
  },
  map: {
    width: '100%',
    height: '100vh',
    zIndex: 1000,
    background: 'linear-gradient(180deg, #87CEEB 0%, #B0E0E6 50%, #F0E68C 100%)',
  },
  gameMapContainer: {
    position: 'relative',
    width: '100%',
    height: '100vh',
    background: 'linear-gradient(180deg, #87CEEB 0%, #B0E0E6 50%, #F0E68C 100%)',
    overflow: 'hidden',
  },
  topDownMapContainer: {
    position: 'relative',
    width: '100%',
    height: '100vh',
    background: 'linear-gradient(180deg, #87CEEB 0%, #B0E0E6 50%, #F0E68C 100%)',
    overflow: 'hidden',
  },
  streetsSVG: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1000,
  },
  buildingsLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1500,
  },
  gameBuilding: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transform: 'translate(-50%, -50%)',
    cursor: 'pointer',
  },
  buildingIcon: {
    fontSize: '40px',
    marginBottom: '5px',
    filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
  },
  buildingLabel: {
    fontSize: '12px',
    color: '#333',
    fontWeight: 'bold',
    background: 'white',
    padding: '2px 8px',
    borderRadius: '10px',
    border: '2px solid #8B7355',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  gameTaxi: {
    position: 'absolute',
    fontSize: '35px',
    transform: 'translate(-50%, -50%)',
    zIndex: 1600,
    filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
    animation: 'bounce 1s ease-in-out infinite',
  },
  mapDecoration: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 2000,
  },
  cloud: {
    position: 'absolute',
    fontSize: '60px',
    opacity: 0.7,
    animation: 'float 6s ease-in-out infinite',
  },
  sun: {
    position: 'absolute',
    top: '5%',
    right: '5%',
    fontSize: '80px',
  },
  conversationScene: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to bottom, #87CEEB 0%, #87CEEB 60%, #C2B280 60%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4000,
  },
  conversationOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4500,
    pointerEvents: 'none',
  },
  menuScene: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to bottom, #87CEEB 0%, #87CEEB 60%, #C2B280 60%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 4000,
  },
  characterContainer: {
    position: 'relative',
    fontSize: '120px',
    marginBottom: '30px',
  },
  character: {
    fontSize: '120px',
  },
  actionButton: {
    position: 'absolute',
    top: '-40px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    fontSize: '24px',
    cursor: 'pointer',
    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
  },
  speechBubble: {
    background: 'white',
    borderRadius: '20px',
    padding: '20px 30px',
    maxWidth: '600px',
    textAlign: 'center',
    fontSize: '18px',
    color: '#333',
    marginBottom: '30px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
    pointerEvents: 'auto',
  },
  choicesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    maxWidth: '500px',
    pointerEvents: 'auto',
  },
  choiceButton: {
    padding: '15px 30px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '15px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    textAlign: 'left',
    pointerEvents: 'auto',
  },
  menuContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    maxWidth: '1200px',
    marginTop: '20px',
  },
  menuSection: {
    background: 'white',
    borderRadius: '15px',
    padding: '20px',
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px',
    borderBottom: '1px solid #eee',
    cursor: 'pointer',
  },
  orderButtons: {
    marginTop: '20px',
  },
  hud: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    background: 'rgba(0, 0, 0, 0.8)',
    border: '3px solid #FFD700',
    borderRadius: '15px',
    padding: '20px',
    zIndex: 5000,
    color: 'white',
    minWidth: '250px',
  },
  hudItem: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
    paddingBottom: '10px',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
  },
  cart: {
    position: 'fixed',
    right: '20px',
    top: '200px',
    background: 'white',
    borderRadius: '15px',
    padding: '20px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
    minWidth: '250px',
    maxHeight: '400px',
    overflowY: 'auto',
    zIndex: 5000,
  },
  popup: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'white',
    border: '4px solid #FFD700',
    borderRadius: '20px',
    padding: '30px',
    zIndex: 6000,
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    textAlign: 'center',
    minWidth: '350px',
  },
  actionMenu: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'white',
    border: '4px solid #4CAF50',
    borderRadius: '20px',
    padding: '20px',
    zIndex: 6000,
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    minWidth: '300px',
  },
  actionMenuItem: {
    width: '100%',
    padding: '12px',
    margin: '8px 0',
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '10px',
    fontSize: '16px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  loading: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  topDownMapContainer: {
    position: 'relative',
    width: '100%',
    height: '100vh',
    background: 'linear-gradient(180deg, #87CEEB 0%, #B0E0E6 40%, #90EE90 60%, #F0E68C 100%)',
    overflow: 'hidden',
  },
  streetsSVG: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  buildingsLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 2,
  },
  gameBuilding: {
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    transition: 'transform 0.3s',
  },
  buildingIcon: {
    fontSize: '60px',
    marginBottom: '5px',
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
  },
  buildingLabel: {
    background: 'white',
    padding: '8px 15px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333',
    border: '2px solid #8B7355',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    whiteSpace: 'nowrap',
  },
  gameTaxi: {
    position: 'absolute',
    fontSize: '50px',
    transform: 'translate(-50%, -50%)',
    zIndex: 3,
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
    animation: 'bounce 0.5s infinite',
  },
};

// Add CSS animations
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes bounce {
      0%, 100% { transform: translate(-50%, -50%) translateY(0); }
      50% { transform: translate(-50%, -50%) translateY(-10px); }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-20px); }
    }
  `;
  document.head.appendChild(style);
}

export default MoroccoConversationGame;
